import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LEADERBOARD_WINDOWS, type LeaderboardWindow } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconReports } from '../icons';
import { money } from '../money';

const WINDOW_LABEL: Record<LeaderboardWindow, string> = {
  week: 'This week',
  month: 'This month',
};

/**
 * The technician leaderboard (D6) — the FIRST consumer of `report.leaderboard`, which
 * ticket 111 shipped server-only.
 *
 * ⚠️ Three things the approved prototype draws that this deliberately does not, because
 * the payload does not contain them and a board that invents them is worse than one that
 * is plainer:
 *
 *   · **A points column.** `LeaderboardRow` has no composite score, and its key set is
 *     pinned by a test that fails on purpose when a field is added. The prototype's
 *     footnote says "four metrics, weighted equally", which is not a definition: jobs
 *     (18), on-time (96%), evidence (94%) and money ($4,820) have four different units,
 *     and any single number over them needs a normalisation nobody has chosen. Choosing
 *     one HERE would put a product rule in a browser, where the shop cannot see it and
 *     the server cannot check it. It is a 111 follow-up, not a render.
 *   · **A podium.** A podium asserts a ranking. The server sorts by jobs, then money,
 *     then name — deterministic, but two people level on all three are genuinely
 *     indistinguishable in the payload, and a gold ring over one of them would be an
 *     invention. Rank is shown as position, which is exactly what the array is.
 *   · **Re-sorting by a column.** The order is the server's, with an ICU `'en'` collator
 *     pinned so a laptop and a container agree. A client-side sort would be a second
 *     opinion with a different collator.
 *
 * ⭐ `null` renders as an em dash, never as `0`. `0/0` is not 0% and it is not 100%; a
 * shop that has never recorded an arrival must not be shown a perfect score it did not
 * earn — and `?? 0` and a truthiness check both destroy a legitimate 0%.
 */
export function FleetLeaderboard() {
  const trpc = useTRPC();
  const [window, setWindow] = useState<LeaderboardWindow>('month');
  const query = useQuery(trpc.report.leaderboard.queryOptions({ window }));
  const state = shownState(query, query.data);
  const data = query.data;

  return (
    <section className="rounded-[var(--radius)] border border-border bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <h2 className="font-heading text-sm font-semibold">Technician leaderboard</h2>
        {/*
         * Radio group, not a select: two options, and the whole point is that the reader
         * can see which period the numbers are for without opening anything.
         */}
        <div role="group" aria-label="Leaderboard period" className="flex gap-1">
          {LEADERBOARD_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWindow(w)}
              aria-pressed={window === w}
              className={`rounded-full px-3 py-1 text-xs focus-visible:ring-2 focus-visible:ring-ring/50 ${
                window === w
                  ? 'bg-brand text-white'
                  : 'border border-border text-muted-foreground'
              }`}
            >
              {WINDOW_LABEL[w]}
            </button>
          ))}
        </div>
      </header>

      {!data ? (
        <div className="p-4">
          <UnresolvedState
            state={unresolved(state)}
            what="the leaderboard"
            icon={IconReports}
            error={query.error}
            onRetry={() => void query.refetch()}
            skeleton="h-48"
          />
        </div>
      ) : (
        <>
          {/*
           * 🔴 A board that failed to refresh is not merely old. These are people's
           * numbers, read as current and compared between them — a month's collected
           * total that quietly stopped updating is one a shop would act on. The notice
           * covers the only query this card draws from.
           */}
          <div className="px-4 pt-3">
            <StaleNotice
              show={refreshFailed(query, query.data)}
              onRetry={() => void query.refetch()}
            />
          </div>
          {/*
           * ⭐ The basis renders WITH the numbers, never in a tooltip and never inferred
           * here. The dates are the shop's, resolved in the shop's zone by the server —
           * a browser computing "this month" would put a second clock on a board whose
           * whole value is that everyone is looking at the same one.
           */}
          <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground">
            <span className="font-medium">
              {data.basis.from} to {data.basis.to}
            </span>{' '}
            · {data.basis.population}
            {data.basis.tipsIncluded ? '' : ' · tips are not counted'}
          </p>

          {data.rows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              Nothing to score for this period yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Technician
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Jobs completed
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      On-time arrival
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Evidence discipline
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Collected in field
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.userId} className="border-b border-border last:border-b-0">
                      <th scope="row" className="px-4 py-2 text-left font-medium">
                        <span className="tabular-nums text-muted-foreground">
                          {i + 1}
                        </span>{' '}
                        {r.name}
                      </th>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {r.jobsCompleted}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        <Share pct={r.onTimePct} measured={r.onTimeMeasured} />
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        <Share pct={r.evidencePct} measured={r.evidenceMeasured} />
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {money(r.collectedCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
           * ⭐ EVERY caveat, un-sliced. They are the part of the board that says what it
           * cannot tell you, and a board that shows two of five is a board that has been
           * edited. D6's contract is that techs see this and that it states its own
           * limits — dropping the limits keeps the disclosure and loses the honesty.
           */}
          {data.caveats.length > 0 ? (
            <div className="border-t border-border px-4 py-3">
              {/*
               * A HEADING, not a `<details>`. The contract is "rendered, not buried", so
               * these stay open — but unlabelled they read as a wall of grey text nobody
               * can place, and text nobody can place is buried by a different route. The
               * heading says what the block is so a reader can choose to read it.
               */}
              <h3 className="text-xs font-medium">
                What this board does and does not count
              </h3>
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {data.caveats.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * A percentage beside the count it was taken over.
 *
 * ⭐ `pct === null` explicitly. `pct ?? 0` would turn "we have never measured this" into
 * "they were never on time", and `pct ? … : …` would do the same to a real, earned 0%.
 * The denominator sits next to it because "100%" over one arrival is not wrong, it is
 * unreadable.
 */
function Share({ pct, measured }: { pct: number | null; measured: number }) {
  if (pct === null) {
    return (
      <span className="text-muted-foreground" title="Nothing measured in this period">
        —
      </span>
    );
  }
  return (
    <>
      {pct}% <span className="text-xs text-muted-foreground">({measured} measured)</span>
    </>
  );
}
